import { useIntl } from 'react-intl';

import type { AegSupportHistoryController } from '../../../../hooks/useAegSupportHistory';
import { formatDateTimeWithTimezone } from '../../../../utils/dateUtils';
import { AEG_FIELD_LABELS, type AegHistoryEntry } from '../../../../utils/aegSupportHistory';
import { allStages } from '../../config';
import { type AegEnumGroup, formatAegEnum, formatAegStage } from '../../types';

const ENUM_FIELDS: Record<string, AegEnumGroup> = {
    status: 'status',
    requestType: 'requestType',
    approvalType: 'approvalType',
    vehicleType: 'vehicleType',
    serviceType: 'serviceType',
    repairScope: 'repairScope',
    moveTo: 'status',
    attendanceCondition: 'attendanceCondition',
    implementOrBodyBuilder: 'implementOrBodyBuilder',
    bodyBuilderName: 'bodyBuilderName',
};
import Select from '../Select';
import './styles.css';

const DATE_FIELDS = ['repairDate', 'lastVwcoManPurchase'];

type SupportHistoryProps = AegSupportHistoryController;

export default function SupportHistory({
    entries,
    loading,
    filters,
    updateFilters,
    applyFilters,
}: SupportHistoryProps) {
    const intl = useIntl();
    const t = (id: string) => intl.formatMessage({ id: `intl-msg:${id}` });
    const translate = (id: string) => intl.formatMessage({ id });

    const fieldLabel = (field: string) =>
        field === 'moveTo'
            ? translate(AEG_FIELD_LABELS.status)
            : field.startsWith('intl-msg:')
            ? translate(field)
            : AEG_FIELD_LABELS[field]
              ? translate(AEG_FIELD_LABELS[field])
              : field;

    const formatValue = (entry: AegHistoryEntry, value: string) => {
        if (!value) {
            return '';
        }
        const enumGroup = ENUM_FIELDS[entry.field];
        if (enumGroup) {
            return formatAegEnum(intl, enumGroup, value) || value;
        }
        const normalized = value.toLowerCase();
        if (['true', 'false', 'yes', 'no'].includes(normalized)) {
            return t(`support.aeg.detail.history.${['true', 'yes'].includes(normalized) ? 'yes' : 'no'}`);
        }
        if (DATE_FIELDS.includes(entry.field)) {
            return formatDateTimeWithTimezone(value);
        }
        return value;
    };

    const hasValues = (entry: AegHistoryEntry) => !!(entry.old || entry.new);

    return (
        <div className='aeg-history display-flex flex-column gap-15'>
            <div className='aeg-history-filters'>
                <label className='display-flex flex-column gap-5 margin-0'>
                    <span className='text-size-12 text-medium text-color-darker'>{t('tas.occurrence.step')}</span>
                    <Select
                        ariaLabel={t('tas.occurrence.step')}
                        emptyLabel={t('support.aeg.detail.history.allStages')}
                        fullWidth
                        onChange={value => updateFilters({ stage: value as typeof filters.stage })}
                        options={allStages.map(stage => ({ value: stage, label: formatAegStage(intl, stage) }))}
                        value={filters.stage}
                    />
                </label>
                <label className='display-flex flex-column gap-5 margin-0'>
                    <span className='text-size-12 text-medium text-color-darker'>{t('support.cardStatus.user')}</span>
                    <input
                        className='form-control'
                        onChange={event => updateFilters({ user: event.target.value })}
                        onKeyDown={event => event.key === 'Enter' && applyFilters()}
                        placeholder={t('support.cardStatus.user')}
                        type='text'
                        value={filters.user}
                    />
                </label>
                <label className='display-flex flex-column gap-5 margin-0'>
                    <span className='text-size-12 text-medium text-color-darker'>
                        {t('support.aeg.detail.history.from')}
                    </span>
                    <input
                        className='form-control'
                        max={filters.to || undefined}
                        onChange={event => updateFilters({ from: event.target.value })}
                        type='date'
                        value={filters.from}
                    />
                </label>
                <label className='display-flex flex-column gap-5 margin-0'>
                    <span className='text-size-12 text-medium text-color-darker'>
                        {t('support.aeg.detail.history.to')}
                    </span>
                    <input
                        className='form-control'
                        min={filters.from || undefined}
                        onChange={event => updateFilters({ to: event.target.value })}
                        type='date'
                        value={filters.to}
                    />
                </label>
                <button className='btn btn-primary' onClick={applyFilters} type='button'>
                    {t('support.supportHistory.applyFilter')}
                </button>
            </div>

            {loading ? (
                <div className='display-flex align-items-center justify-content-center padding-30 text-color-dark'>
                    <span className='spinner margin-right-10'>
                        <span />
                        <span />
                        <span />
                        <span />
                        <span />
                        <span />
                        <span />
                    </span>
                    {t('support.aeg.detail.history.loading')}
                </div>
            ) : entries.length ? (
                <ol className='aeg-history-timeline margin-0 padding-0'>
                    {entries.map(entry => (
                        <li key={entry.key}>
                            <span aria-hidden='true' className='aeg-history-dot rioglyph rioglyph-ok' />
                            <div className='aeg-history-box border rounded padding-10'>
                                <dl className='aeg-history-meta margin-0'>
                                    <div>
                                        <dt>{t('support.supportHistory.userLabel')}</dt>
                                        <dd>{entry.user}</dd>
                                    </div>
                                    <div>
                                        <dt>{t('support.supportHistory.dateTimeLabel')}</dt>
                                        <dd>{formatDateTimeWithTimezone(entry.createdAt)}</dd>
                                    </div>
                                    <div>
                                        <dt>{t('support.supportHistory.stepLabel')}</dt>
                                        <dd>{entry.stage ? formatAegStage(intl, entry.stage) : '—'}</dd>
                                    </div>
                                    <div>
                                        <dt>{t('support.aeg.detail.history.changedField')}</dt>
                                        <dd>{fieldLabel(entry.field)}</dd>
                                    </div>
                                </dl>
                                {hasValues(entry) && (
                                    <div className='aeg-history-diff'>
                                        <div>
                                            <b>{t('support.supportHistory.previousValue')}</b>
                                            {formatValue(entry, entry.old) || '—'}
                                        </div>
                                        <div className='new'>
                                            <b>{t('support.supportHistory.newValue')}</b>
                                            {formatValue(entry, entry.new) || '—'}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </li>
                    ))}
                </ol>
            ) : (
                <div className='padding-30 border border-style-dashed rounded text-center text-color-dark'>
                    {t('support.supportHistory.noLogs')}
                </div>
            )}
        </div>
    );
}
